import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import { useDirtyStore } from '@/stores/dirty';
import { usePipelineBuilderStore } from '@/stores/pipeline-builder';
import { useTransformBuilderStore } from '@/stores/transform-builder';
import { useDashboardEditStore } from '@/stores/dashboard-edit';
import { useSeedDataStore } from '@/stores/seed-data';
import { useExplorerStore } from '@/stores/explorer';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { api } from '@/lib/api';
import type { Environment } from '@solidafy/api-client';

/**
 * Given the current pathname, return the section root so that switching
 * environments doesn't carry stale resource IDs from the previous env.
 * e.g. /pipelines/abc-123/edit → /pipelines, /bi/abc-123 → /bi
 */
function getSectionRoot(pathname: string): string | null {
  const sections = [
    '/pipelines',
    '/data-sources',
    '/bi',
    '/notebooks',
    '/seed-data',
    '/catalog',
    '/queries',
    '/transforms',
    '/settings/account',
    '/settings/organization',
    '/settings/environments',
    '/settings/api-keys',
    '/settings/notifications',
    '/settings/security-groups',
    '/settings/sso',
    '/settings/policies',
    '/settings/admin',
    '/coordinators',
    '/jobs',
    '/ai',
    '/settings',
  ];
  for (const section of sections) {
    if (pathname.startsWith(section + '/') || pathname === section) {
      return section;
    }
  }
  return null;
}

type GroupedOrg = {
  id: string;
  name: string;
  environments: Environment[];
};

export function EnvironmentSelector() {
  const { isAuthenticated, setActiveOrg, getCurrentUser } = useAuthStore();
  const queryClient = useQueryClient();
  const isDirty = useDirtyStore((s) => s.isDirty);

  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [pendingEnvId, setPendingEnvId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const fetched = useRef(false);

  const fetchEnvironments = useCallback(async () => {
    if (!isAuthenticated()) {
      return;
    }
    const { data, error } = await api.api.v1.environments.get();
    if (error) {
      console.error('Failed to fetch environments:', error);
      return;
    }
    const envs = data ?? [];
    setEnvironments(envs);
    if (envs.length > 0) {
      const active = envs.find((e) => e.is_user_active) || envs.find((e) => e.is_default) || envs[0];
      setSelectedId(active.id);
      if (active.organization_id) {
        setActiveOrg(active.organization_id);
      }
    }
  }, [setActiveOrg, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated() && !fetched.current) {
      fetched.current = true;
      setTimeout(() => void fetchEnvironments(), 0);
    }
  }, [isAuthenticated, fetchEnvironments]);

  useEffect(() => {
    const handler = () => void fetchEnvironments();
    window.addEventListener('environment-created', handler);
    return () => window.removeEventListener('environment-created', handler);
  }, [fetchEnvironments]);

  const performSwitch = useCallback(
    async (newId: string) => {
      const previousId = selectedId;
      setSelectedId(newId);
      setSwitching(true);

      const switched = environments.find((e) => e.id === newId);
      if (switched?.organization_id) {
        setActiveOrg(switched.organization_id);
      }

      const { error } = await api.api.v1.environments.switch.post({ environment_id: newId });
      if (error) {
        console.error('Failed to switch environment:', error);
        setSelectedId(previousId);
        setSwitching(false);
        return;
      }

      // Refresh user so the new env's role + permissions are reflected
      await getCurrentUser();

      // Invalidate all cached data — mounted queries refetch immediately
      await queryClient.invalidateQueries();

      // Reset env-scoped stores
      usePipelineBuilderStore.getState().resetBuilder();
      useTransformBuilderStore.getState().resetStore();
      useDashboardEditStore.getState().exitEditMode();
      useSeedDataStore.getState().reset();
      useExplorerStore.getState().reset();
      useDirtyStore.getState().setDirty(false);

      // Navigate to section root so stale resource IDs/params from the old env are dropped
      const currentPath = window.location.pathname;
      const sectionRoot = getSectionRoot(currentPath);
      if (sectionRoot) {
        if (currentPath !== sectionRoot || window.location.search) {
          // Use window.location for same-path navigation to force full remount
          // (React Router's navigate won't remount if pathname is unchanged)
          window.location.replace(sectionRoot);
          return;
        }
      }

      setSwitching(false);
    },
    [selectedId, environments, setActiveOrg, getCurrentUser, queryClient],
  );

  const handleSwitch = async (newId: string) => {
    if (newId === selectedId) return;

    if (isDirty) {
      setPendingEnvId(newId);
      setConfirmOpen(true);
      return;
    }

    await performSwitch(newId);
  };

  const handleConfirmSwitch = () => {
    setConfirmOpen(false);
    if (pendingEnvId) {
      void performSwitch(pendingEnvId);
      setPendingEnvId(null);
    }
  };

  const handleCancelSwitch = () => {
    setConfirmOpen(false);
    setPendingEnvId(null);
  };

  // Group by organization
  const grouped: GroupedOrg[] = [];
  const orgMap: Record<string, GroupedOrg> = {};
  for (const env of environments) {
    const orgId = env.organization_id || 'unknown';
    if (!orgMap[orgId]) {
      orgMap[orgId] = { id: orgId, name: env.organization_name || 'Organization', environments: [] };
      grouped.push(orgMap[orgId]);
    }
    orgMap[orgId].environments.push(env);
  }

  if (environments.length === 0) return null;

  return (
    <>
      <div className="environment-selector" data-tour="env-selector">
        <select
          className="env-select"
          value={selectedId}
          disabled={switching}
          onChange={(e) => {
            void handleSwitch(e.target.value);
          }}
          aria-label="Select environment"
        >
          {grouped.map((org) => (
            <optgroup key={org.id} label={org.name}>
              {org.environments.map((env) => (
                <option key={env.id} value={env.id}>
                  {env.name} ({env.type})
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title="Switch environment?"
        message="You have unsaved changes that will be lost."
        detail="Save your work before switching, or continue to discard changes."
        confirmLabel="Switch anyway"
        confirmVariant="danger"
        onConfirm={handleConfirmSwitch}
        onCancel={handleCancelSwitch}
      />
    </>
  );
}
